import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Quote } from "lucide-react";
import { useLenis } from "../hooks/useLenis";
import { useSeo } from "../hooks/useSeo";
import { ApmJoinModal } from "../components/apm/ApmJoinModal";

const ease = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.8, delay, ease }}
  >
    {children}
  </motion.div>
);

const H2 = ({ children }) => (
  <Reveal>
    <h2 className="font-display font-light text-white text-[clamp(24px,3.2vw,40px)] leading-[1.14] tracking-[-0.02em] mt-16 mb-6">
      {children}
    </h2>
  </Reveal>
);

const P = ({ children }) => (
  <Reveal>
    <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-5">{children}</p>
  </Reveal>
);

const BULLETS = [
  "jak zmienić pomysł w konkretny plan działania,",
  "jak podejmować trafniejsze decyzje,",
  "jak unikać kosztownych błędów,",
  "jak rozwijać swoje źródła dochodu,",
  "jak budować wartościowe kontakty,",
  "jak konsekwentnie przechodzić na kolejny poziom.",
];

export default function ApmHistoria() {
  useLenis();
  useSeo({
    title: "Moja historia — Szymon | APM",
    description: "Zacząłem wcześniej niż większość. Mam 23 lata — poznaj historię założyciela APM: pierwsze kroki w biznesie w wieku 17 lat, pierwszy Mercedes, BMW M2 i droga, którą możesz skrócić dzięki APM.",
    canonical: "https://pmpoint.pl/apm/moja-historia",
  });
  const [modal, setModal] = useState(false);

  return (
    <div className="relative min-h-screen text-white overflow-x-hidden" style={{ background: "#030206" }} data-testid="apm-historia-page">
      <div className="apm-noise" aria-hidden="true" />
      <div className="apm-aurora" aria-hidden="true">
        <span className="a1" /><span className="a2" /><span className="a3" />
      </div>

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 border-b border-white/[0.06] bg-[#030206]/60 backdrop-blur-2xl">
        <nav className="container-pm flex items-center justify-between h-[74px]">
          <Link to="/apm" className="font-display font-medium text-xl tracking-[0.32em] text-white" data-testid="historia-logo">APM</Link>
          <Link to="/apm" className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-white/60 hover:text-white transition-colors" data-testid="historia-back">
            <ArrowLeft size={14} /> Wróć do APM
          </Link>
          <button
            onClick={() => setModal(true)}
            data-testid="historia-nav-join"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur text-white text-[11px] uppercase tracking-[0.16em] hover:bg-white/[0.08] transition-all"
          >
            Dołącz
          </button>
        </nav>
      </header>

      {/* HERO */}
      <section className="relative pt-40 pb-16 sm:pt-48 sm:pb-20 border-b border-white/[0.06] overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-0 w-[520px] h-[520px] rounded-full apm-blob" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.7), transparent 64%)" }} />
        <div className="container-pm relative max-w-[820px]">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#a78bfa]/80 mb-6">Moja historia</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-light text-[clamp(38px,6vw,80px)] leading-[1.02] tracking-[-0.03em]">
              Zacząłem <span className="font-serif-apm italic font-medium text-[#c4b3fb]">wcześniej</span> niż większość.
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-8 flex items-center gap-3">
              <span className="h-px w-10 bg-[#8b5cf6]" />
              <span className="text-sm uppercase tracking-[0.2em] text-white/50">Szymon · Założyciel APM</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ARTICLE */}
      <article className="relative py-[clamp(48px,7vw,90px)]">
        <div className="container-pm max-w-[760px]">
          <P>Mam 23 lata, ale moja historia nie zaczęła się dzisiaj.</P>
          <P>Mając 17 lat, wiedziałem już, że nie chcę czekać, aż ktoś zdecyduje za mnie, jak ma wyglądać moje życie. Chciałem mieć wybór. Chciałem sam decydować, ile zarabiam, z kim pracuję i na jakich zasadach żyję.</P>
          <P>Nie znałem jeszcze wszystkich odpowiedzi. Popełniałem błędy, traciłem czas i wiele rzeczy musiałem odkrywać sam. Zamiast jednak szukać wymówek, szukałem sposobów.</P>
          <P>Uczyłem się, testowałem i działałem.</P>

          <Reveal className="my-12">
            <figure className="rounded-3xl overflow-hidden border border-white/10">
              <img src="/apm/story-01-wiek-17-laptop.png" alt="Pierwsze kroki w biznesie" className="w-full h-[300px] object-cover" />
            </figure>
          </Reveal>

          <H2>Pierwszy rezultat przyszedł szybciej, niż się spodziewałem</H2>
          <P>Mając 18 lat, kupiłem swojego pierwszego Mercedesa.</P>
          <P>Nie chodziło wyłącznie o samochód. Był dla mnie dowodem, że decyzje podejmowane wcześniej zaczynają przynosić efekty. Zobaczyłem, że młody wiek nie musi być ograniczeniem, jeżeli masz wiedzę, plan i odwagę, żeby działać.</P>

          <Reveal className="my-12 grid sm:grid-cols-2 gap-5">
            <figure className="rounded-3xl overflow-hidden border border-white/10">
              <img src="/apm/story-02-pierwszy-mercedes.png" alt="Pierwszy Mercedes" className="w-full h-[240px] object-cover" />
            </figure>
            <figure className="rounded-3xl overflow-hidden border border-white/10">
              <img src="/apm/story-03-bmw-m2.png" alt="BMW M2" className="w-full h-[240px] object-cover" />
            </figure>
          </Reveal>

          <P>Dzisiaj jeżdżę BMW M2, rozwijam kolejne projekty i jestem w trakcie zakupu mieszkania.</P>
          <P>Nie opowiadam Ci tego, żeby się chwalić. Opowiadam, ponieważ chcę Ci pokazać, jak wiele może się zmienić, kiedy przestajesz tylko obserwować innych i zaczynasz świadomie budować własny wynik.</P>

          <H2>Najdroższe były błędy, których mogłem uniknąć</H2>
          <P>Do wielu rzeczy dochodziłem sam. Zapłaciłem za to czasem, pieniędzmi i nietrafionymi decyzjami.</P>
          <P>Dziś wiem, że ta droga mogła być krótsza.</P>
          <P>Gdybym na początku miał dostęp do ludzi, którzy pokazaliby mi, co naprawdę działa, czego unikać i na czym skoncentrować energię, osiągnąłbym wiele celów szybciej.</P>

          <Reveal className="my-12">
            <blockquote className="relative rounded-3xl border border-[#8b5cf6]/25 bg-[#8b5cf6]/[0.05] p-8 sm:p-10">
              <Quote className="text-[#a78bfa] mb-4" size={32} />
              <p className="font-serif-apm italic text-[clamp(22px,3vw,34px)] leading-snug text-white">
                Właśnie dlatego powstało APM.
              </p>
            </blockquote>
          </Reveal>

          <H2>Nie musisz odkrywać wszystkiego sam</H2>
          <P>W APM przekazuję praktyczną wiedzę, doświadczenie i strategie, których sam musiałem uczyć się latami.</P>
          <Reveal>
            <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-5">Pokażę Ci:</p>
          </Reveal>
          <ul className="space-y-3 mb-8">
            {BULLETS.map((b, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full border border-[#8b5cf6]/40 bg-[#8b5cf6]/10 text-[#a78bfa] shrink-0 mt-0.5">
                    <Check size={13} strokeWidth={2.5} />
                  </span>
                  <span className="text-white/70 text-base sm:text-lg">{b}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <P>Nie dostaniesz pustych haseł ani obietnicy sukcesu bez wysiłku. Otrzymasz wiedzę, coaching, wsparcie i środowisko ludzi, którzy chcą od życia czegoś więcej.</P>

          <H2>Teraz decyzja należy do Ciebie</H2>
          <P>Możesz przez kolejne miesiące zastanawiać się, od czego zacząć. Możesz próbować wszystkiego sam i płacić za każdy błąd własnym czasem.</P>
          <P>Możesz też wejść do APM i skorzystać z wiedzy osób, które już przeszły tę drogę.</P>

          <Reveal className="my-10">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur p-8 text-center">
              <p className="text-[11px] uppercase tracking-[0.24em] text-white/45 mb-3">Dostęp do grupy</p>
              <p className="font-display font-light text-white leading-none">
                <span className="text-[clamp(48px,8vw,88px)] tracking-tight">149 zł</span>
                <span className="text-lg text-white/60"> / miesięcznie</span>
              </p>
            </div>
          </Reveal>

          <P>Czasami jedna właściwa informacja, jeden wartościowy kontakt albo jedna trafniejsza decyzja wystarczą, żeby odzyskać znacznie więcej niż koszt członkostwa.</P>
          <P>Nie mogę obiecać Ci konkretnego wyniku — nikt uczciwy nie powinien tego robić. Mogę jednak pokazać Ci drogę, przekazać narzędzia i pomóc uniknąć błędów, za które sam wcześniej zapłaciłem.</P>
        </div>
      </article>

      {/* CTA */}
      <section className="relative py-[clamp(70px,10vw,140px)] border-t border-white/[0.06] overflow-hidden" style={{ background: "#0A0812" }}>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="w-[70%] h-[70%] rounded-full apm-blob" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.9), transparent 64%)", opacity: 0.3 }} />
        </div>
        <div className="container-pm relative text-center max-w-[900px] mx-auto">
          <Reveal>
            <h2 className="font-display font-light text-white text-[clamp(32px,5.5vw,72px)] leading-[1.0] tracking-[-0.03em]">
              Twoja historia może zacząć się{" "}
              <span className="font-serif-apm italic font-medium text-[#c4b3fb] apm-text-glow">właśnie teraz.</span>
            </h2>
            <button
              onClick={() => setModal(true)}
              data-testid="historia-cta-join"
              className="btn-premium group mt-10 inline-flex items-center gap-3 h-[60px] px-9 rounded-full bg-[#8b5cf6] text-white text-[13px] uppercase tracking-[0.1em] font-semibold"
            >
              Dołącz do elitarnej grupy APM za 149 zł / mies. <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-6">
              <Link to="/apm" className="text-sm text-white/45 hover:text-[#a78bfa] transition-colors">← Wróć na stronę APM</Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative py-14 border-t border-white/[0.06]" style={{ background: "#030206" }}>
        <div className="container-pm flex flex-col md:flex-row items-center justify-between gap-5">
          <span className="font-display font-medium text-lg tracking-[0.3em] text-white">APM</span>
          <span className="text-[11px] text-white/30">
            © {new Date().getFullYear()} APM · PMPOINT · Design &amp; copyright by{" "}
            <a href="https://apisdesign.pl" target="_blank" rel="noopener noreferrer" className="text-white/45 hover:text-[#a78bfa] transition-colors">apisdesign.pl</a>
          </span>
        </div>
      </footer>

      <ApmJoinModal open={modal} onClose={() => setModal(false)} />
    </div>
  );
}
